import React, { useState, useCallback } from 'react';
import { Typography, Stack, Box } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import EmptyPieChart from './EmptyPieChart';

interface DonutCardProps {
  title: string;
  data: Array<{ name: string; value: number }>;
  colors: string[];
  cardWidth?: number;
  chartHeight?: number;
}

// --- Custom Interactive Legend Component ---
interface CustomLegendProps {
  data: Array<{ name: string; value: number }>;
  colors: string[];
  hiddenSegments: Record<string, boolean>;
  toggleSegment: (name: string) => void;
}

const CustomLegend: React.FC<CustomLegendProps> = ({ data, colors, hiddenSegments, toggleSegment }) => (
  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', justifyContent: 'center' }}>
    {data.map((e, i) => {
      const h = hiddenSegments[e.name];
      return (
        <Stack
          key={e.name}
          direction="row"
          spacing={1}
          alignItems="center"
          onClick={() => toggleSegment(e.name)}
          sx={{ cursor: 'pointer', opacity: h ? 0.5 : 1, userSelect: 'none' }}
        >
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: colors[i] }} />
          <Typography
            variant="caption"
            sx={{
              color: '#333',
              whiteSpace: 'nowrap',
              fontSize: 12,
              fontWeight: 500,
              textDecoration: h ? 'line-through' : 'none',
              letterSpacing: -0.1,
            }}
          >
            {e.name.replace(/^client\s*/i, '').replace(/^dm\s*/i, '')}
          </Typography>
        </Stack>
      );
    })}
  </Stack>
);

const DonutCard = ({ title, data, colors, cardWidth, chartHeight }: DonutCardProps) => {
  const [hiddenSegments, setHiddenSegments] = useState<Record<string, boolean>>({});

  const toggleSegment = useCallback((name: string) => {
    setHiddenSegments((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  }, []);

  const visibleData = data.filter((d) => !hiddenSegments[d.name]);
  const visibleColors = colors.filter((_, index) => {
    const name = data[index]?.name;
    return name ? !hiddenSegments[name] : false;
  });

  const total = data?.reduce((acc, d) => acc + d.value, 0);
  const hasZeroValue = visibleData.some((d) => d.value === 0);
  const dynamicMinAngle = hasZeroValue ? 0 : 5;

  return (
    <>
      <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
        {title}
      </Typography>
      {!data || total === 0 ? (
        <EmptyPieChart height={chartHeight} data={data} colors={colors} />
      ) : (
        <Stack spacing={1} alignItems="center">
          <Box
            sx={{
              width: cardWidth || 540,
              height: chartHeight,
              position: 'relative',
              zIndex: 1,
              overflow: 'visible',
              mb: -1,
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              {/* Added margin to prevent labels from hitting the edges */}
              <PieChart margin={{ top: 20, bottom: 20, left: 40, right: 40 }} style={{ overflow: 'visible' }}>
                <Pie
                  style={{ overflow: 'visible' }}
                  dataKey="value"
                  isAnimationActive={false}
                  data={visibleData}
                  cx="50%"
                  cy="50%"
                  innerRadius={45}
                  outerRadius={70}
                  stroke="none"
                  minAngle={dynamicMinAngle}
                  labelLine={false}
                  startAngle={180}
                  endAngle={-180}
                  label={({ cx, cy, midAngle, outerRadius, value }) => {
                    const currentTotal = visibleData.reduce((a, b) => a + b.value, 0);
                    if (!currentTotal) return null;

                    const percent = (value / currentTotal) * 100;
                    if (percent === 0) return null;

                    const RADIAN = Math.PI / 180;
                    const sx = cx + outerRadius * Math.cos(-midAngle * RADIAN);
                    const sy = cy + outerRadius * Math.sin(-midAngle * RADIAN);
                    const ex = cx + (outerRadius + 15) * Math.cos(-midAngle * RADIAN);
                    const ey = cy + (outerRadius + 15) * Math.sin(-midAngle * RADIAN);

                    // Safety Check: Hide label if it's too close to vertical bounds
                    // (Adjust the 5px threshold as needed)
                    if (ey < 5 || ey > chartHeight - 5) return null;

                    return (
                      <g style={{ zIndex: 10 }}>
                        <line x1={sx} y1={sy} x2={ex} y2={ey} stroke="#999" strokeWidth={1} />
                        <text
                          x={ex}
                          y={ey}
                          fill="#333"
                          fontSize={12}
                          fontWeight={500}
                          textAnchor={ex > cx ? 'start' : 'end'}
                          dominantBaseline="central"
                          letterSpacing={-0.5}
                        >
                          {value}
                          {`(${percent.toFixed(1)}%)`}
                        </text>
                      </g>
                    );
                  }}
                >
                  {visibleData.map((entry, index) => (
                    <Cell
                      key={`cell-${entry.name}`} // ✅ Use a unique property like name
                      fill={visibleColors[index]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  wrapperStyle={{ zIndex: 1000 }}
                  contentStyle={{ fontSize: 10, padding: '4px 6px', borderRadius: 6 }}
                />
              </PieChart>
            </ResponsiveContainer>
          </Box>

          <CustomLegend data={data} colors={colors} hiddenSegments={hiddenSegments} toggleSegment={toggleSegment} />
        </Stack>
      )}
    </>
  );
};

export default DonutCard;
